// ============================================================
// REACTION AVAILABILITY - may this combatant Dodge? May they Parry?
//
// ONE answer, for every caller. It used to be three: the defence chain
// consulted the condition table, while the two places that decide
// whether to OFFER a defence at all - `useAttackExecution` (does this
// attack prompt?) and `CombatLogPanel` (which buttons appear?) - each
// wrote `frenzied ? false : target.dodge.canDodge` themselves. That
// expression predates conditions existing, so an Immobilised combatant
// was offered a Dodge, took it, and was refused only if they tried to
// react a second time in the same chain.
//
// Three answers to one question is how a rules engine drifts. The rule
// in dev-docs/gotchas.md is that a new consumer must ROUTE THROUGH the
// existing table rather than recompute it; this is that table for
// reactions.
//
// Pure. Zero React dependency.
// ============================================================
import { getConditionReactionState } from './conditionModifiers';
import type { ConditionModOptions, ConditionModContext } from './conditionModifiers';

/**
 * What this needs of a target, and no more.
 *
 * Structural rather than `Target`, following the convention the character
 * bridge already uses: `Target` satisfies it, and so does a half-built
 * projection or a test fixture, which is the point - the attack prompt
 * asks this question before the roster has necessarily settled.
 */
export interface ReactionContext extends ConditionModContext {
  dodge?: { canDodge?: boolean };
  parry?: { canParry?: boolean };
  hasBattleRage?: boolean;
}

export interface ReactionAvailabilityOptions {
  /** GM house rule: do a combatant's conditions gate and penalise reactions? */
  applyConditionMods?: boolean;
  /** Which of those conditions the table has switched off. */
  condOptions?: ConditionModOptions;
}

export interface ReactionAvailability {
  canDodge: boolean;
  canParry: boolean;
  /** Skill penalty to apply if the reaction is taken (≤ 0). */
  dodgeMod: number;
  parryMod: number;
  /**
   * Condition names that took a reaction away, for the UI to explain
   * itself with. EMPTY when the reason is merely that the sheet says
   * untrained - that is not a condition and must not read like one.
   */
  disabledBy: string[];
}

const NONE: ReactionAvailability = { canDodge: false, canParry: false, dodgeMod: 0, parryMod: 0, disabledBy: [] };

/** The zeroed state used when the house rule is off - behaviour unchanged. */
const NO_CONDITIONS = {
  dodge: { disabled: false, mod: 0 },
  parry: { disabled: false, mod: 0 },
  disableSources: [] as string[],
};

/**
 * May this combatant react, and at what penalty?
 *
 * Tolerant of an absent or half-built target: the attack prompt asks this
 * before the roster has necessarily settled, and a projection must never
 * throw on that path.
 */
export function resolveReactionAvailability(
  target: ReactionContext | null | undefined,
  options: ReactionAvailabilityOptions = {},
): ReactionAvailability {
  if (!target) return { ...NONE };

  const cond = options.applyConditionMods ? getConditionReactionState(target, options.condOptions) : NO_CONDITIONS;

  // Frenzy blocks a dodge outright and a parry unless Battle Rage
  // re-enables it. Condition gates stack on top rather than replacing it.
  const frenzied = target.frenzied || false;
  const hasBattleRage = target.hasBattleRage || false;

  return {
    canDodge: frenzied || cond.dodge.disabled ? false : !!target.dodge?.canDodge,
    canParry: (frenzied && !hasBattleRage) || cond.parry.disabled ? false : !!target.parry?.canParry,
    dodgeMod: cond.dodge.mod,
    parryMod: cond.parry.mod,
    disabledBy: [...cond.disableSources],
  };
}

/**
 * THE REACTIONS AGAINST THIS ATTACK: what the defender can take, after the attacker's denials (Unerring takes the
 * dodge; Flexible, or Ethereal against a mundane blade, the parry) and with a Parry only meeting melee unless the
 * defender can parry ranged attacks. The one rule for both sides (2026-10-30): the attacker prompted for a reaction
 * whenever the sheet could parry at all, so a shot at a defender who could only parry asked for "No Defence".
 */
export function reactionsAgainst(
  availability: Pick<ReactionAvailability, 'canDodge' | 'canParry'>,
  denial: { blocksDodge: boolean; blocksParry: boolean },
  attack: { isMelee: boolean; canParryRanged: boolean },
): { canDodge: boolean; canParry: boolean } {
  const reaches = attack.isMelee || attack.canParryRanged;
  return {
    canDodge: !!availability.canDodge && !denial.blocksDodge,
    canParry: !!availability.canParry && !denial.blocksParry && reaches,
  };
}

/**
 * Reasons the SHEET should refuse a deliberate Dodge or Parry test.
 *
 * A reaction and a test rolled from the sheet are not the same act, which
 * is why `PlainTargetTestRow` has always kept derived tests rollable when
 * `canDodge` is false and merely tagged them - being untrained does not
 * stop you trying. A CONDITION is different: "cannot Dodge" forbids the
 * action itself, and offering the button invites a roll that cannot mean
 * anything.
 *
 * Returns only the entries that are actually forbidden, keyed by test id,
 * so a caller can spread it straight into a lookup. EMPTY when the
 * blocking is switched off or the reason is merely an untrained sheet -
 * that is the caller's own tag to render, not a condition.
 */
export function blockedReactionTests(
  target: ReactionContext | null | undefined,
  options: ReactionAvailabilityOptions & { blockSheetTests?: boolean } = {},
): Record<string, string> {
  if (!options.blockSheetTests) return {};
  const { disabledBy, canDodge, canParry } = resolveReactionAvailability(target, options);
  if (disabledBy.length === 0) return {};

  const reason = `${disabledBy.join(', ')} - cannot react`;
  const out: Record<string, string> = {};
  // Guarded on the SHEET's own answer as well: a target who could never
  // dodge is untrained, not forbidden, and must keep its existing tag.
  if (!canDodge && target?.dodge?.canDodge) out.dodge = reason;
  if (!canParry && target?.parry?.canParry) out.parry = reason;
  return out;
}
